import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import BrandMark from "../../components/BrandMark";
import StatusChip from "../../components/StatusChip";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { CheckCircle2, PenLine, ShieldAlert } from "lucide-react";
import { API } from "../../lib/api";

export default function PublicSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signed, setSigned] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/contracts/public/sign/${token}`)
      .then((r) => { setData(r.data); if (r.data.signer?.signed_at) setSigned(r.data.signer); if (r.data.signer?.signed_name) setName(r.data.signer.signed_name); })
      .catch(() => setErr("Contract not found or signing link expired."));
  }, [token]);

  // Canvas signature capture
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0F0F10"; ctx.lineWidth = 2; ctx.lineCap = "round";
    const start = (e) => { drawingRef.current = true; const {x,y} = xy(e); ctx.beginPath(); ctx.moveTo(x,y); };
    const move = (e) => { if (!drawingRef.current) return; const {x,y} = xy(e); ctx.lineTo(x,y); ctx.stroke(); };
    const end = () => { drawingRef.current = false; };
    const xy = (e) => { const r = c.getBoundingClientRect(); const t = e.touches?.[0]; return { x: (t?.clientX ?? e.clientX) - r.left, y: (t?.clientY ?? e.clientY) - r.top }; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); c.addEventListener("mouseup", end); c.addEventListener("mouseleave", end);
    c.addEventListener("touchstart", start); c.addEventListener("touchmove", move); c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move); c.removeEventListener("mouseup", end); c.removeEventListener("mouseleave", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", end);
    };
  }, [data]);

  const clear = () => { const c = canvasRef.current; c?.getContext("2d").clearRect(0,0,c.width,c.height); };

  const submit = async () => {
    if (!name.trim()) { setErr("Type your full legal name."); return; }
    const c = canvasRef.current;
    const dataUrl = c ? c.toDataURL("image/png") : "";
    setBusy(true);
    try {
      const r = await axios.post(`${API}/contracts/public/sign/${token}`, { typed_name: name, signature_data: dataUrl });
      setSigned({ signed_name: name, signed_at: new Date().toISOString(), proof_hash: r.data.proof_hash });
    } catch (e) {
      setErr(e.response?.data?.detail || "Unable to submit signature.");
    } finally { setBusy(false); }
  };

  if (err && !data) return <div className="min-h-screen bg-erf-dark text-erf-cream flex items-center justify-center p-6" data-testid="public-sign-error"><div className="text-center"><BrandMark size={80} /><div className="mt-4 text-erf-cream/70">{err}</div></div></div>;
  if (!data) return <div className="min-h-screen bg-erf-cream flex items-center justify-center font-mono text-xs text-erf-dark/60">Loading contract…</div>;

  const c = data.contract;
  return (
    <div className="min-h-screen bg-erf-cream text-erf-dark" data-testid="public-sign-page">
      <header className="bg-erf-dark text-erf-cream px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3"><BrandMark size={48} /><div>
          <div className="font-display font-bold">ERF ENTERPRISES LLC</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-erf-cream/50">Contract Signature</div>
        </div></div>
        <StatusChip status={c.status === "fully_signed" ? "verified" : "connecting"} label={c.status} />
      </header>

      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs font-nav text-amber-900 flex items-start gap-2" data-testid="public-sign-provider-notice">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <div><strong>Internal-placeholder e-signature.</strong> This platform captures your signature, name, IP, and time-stamp with a cryptographic proof hash chained into the ERF Audit Ledger. It is used for operational tracking. When a DocuSign or Dropbox Sign integration is configured, contracts will be routed through that provider for legally-verified e-signature.</div>
        </div>

        <Card className="border-erf-borderCream">
          <CardContent className="p-6">
            <div className="text-[11px] font-mono uppercase tracking-widest text-erf-gold">Contract</div>
            <div className="mt-3 prose max-w-none text-sm font-nav" dangerouslySetInnerHTML={{ __html: c.contract_body_html || "<p>Contract body missing.</p>" }} />
          </CardContent>
        </Card>

        {signed ? (
          <Card className="border-emerald-200 bg-emerald-50" data-testid="public-sign-complete">
            <CardContent className="p-6 space-y-2">
              <div className="flex items-center gap-2 text-emerald-900 font-display font-semibold text-lg"><CheckCircle2 className="w-5 h-5" /> Signed by {signed.signed_name}</div>
              <div className="mono text-[11px] text-emerald-800">Timestamp: {signed.signed_at}</div>
              {signed.proof_hash && <div className="mono text-[10px] text-emerald-800/70 break-all">Proof hash: {signed.proof_hash}</div>}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-erf-gold/40 bg-erf-gold/5">
            <CardContent className="p-6 space-y-4">
              <div className="font-display font-bold text-lg flex items-center gap-2"><PenLine className="w-5 h-5 text-erf-gold" /> Sign here</div>
              <div>
                <Label className="text-xs uppercase font-nav">Type full legal name</Label>
                <Input value={name} onChange={(e)=>setName(e.target.value)} className="mt-1 bg-white font-mono" data-testid="public-sign-name" />
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Draw signature</Label>
                <div className="mt-1 border border-erf-borderCream bg-white rounded">
                  <canvas ref={canvasRef} width={600} height={160} className="w-full touch-none" data-testid="public-sign-canvas" />
                </div>
                <button type="button" onClick={clear} className="mt-1 text-[11px] font-nav text-erf-gold hover:underline" data-testid="public-sign-clear">Clear</button>
              </div>
              {err && <div className="text-xs text-red-700" data-testid="public-sign-err">{err}</div>}
              <Button onClick={submit} disabled={busy} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="public-sign-submit">
                {busy ? "Submitting…" : "Submit signature"}
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="text-[11px] font-mono text-erf-dark/40 uppercase tracking-widest text-center pt-4 pb-8">
          Chester County, PA · governing law · proof hash chained into audit ledger
        </div>
      </div>
    </div>
  );
}
